import React, {
  useState,
  useEffect,
  useContext,
  useRef,
  useCallback,
} from "react";
import {
  CaretSortIcon,
  ChevronDownIcon,
} from "@radix-ui/react-icons";
import {
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getFacetedRowModel,
  getFacetedUniqueValues,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";

import { Button } from "../../shadcn-components/ui/button";
import { Checkbox } from "../../shadcn-components/ui/checkbox";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../../shadcn-components/ui/dropdown-menu";
import { Input } from "../../shadcn-components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../shadcn-components/ui/table";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../shadcn-components/ui/select";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../../shadcn-components/ui/tooltip";

import clientsData from "../../api/clients";
import { AuthContext } from "../../context/Auth/AuthContext";
import baseUrl from "../../config/baseUrl";
import AddClient from "./AddClient";
import EditClientPopup from "./EditClientPopup";
import BulkDeletePopup from "./BulkDeletePopup";
import BulkDocumentChasingPopup from "./BulkDocumentChasingPopup";
import TableSkeleton from "../common/TableSkeleton";
import PaginationSection from "../common/PaginationSection";
import DataToolbar from "../common/DataToolbar";
import { createStickyColumns } from "../common/stickyColumns";
import getAllTypeList from "../../api/getAllFieldTypeList";
import { useDebounce } from "use-debounce";
import { useRoleContext } from "../../context/RolesContext";
import { convertToUTC, formatDate } from "../../utils/formatDate";
import "../../styles/Clients.css";
import "../../styles/DocumentTable.css";
import FilterListIcon from "@mui/icons-material/FilterList";
import ViewColumnIcon from "@mui/icons-material/ViewColumn";

const ClientsTable = ({
  addClientTab,
  showAddClientForm,
  onAddClientButtonClick,
  clientId,
  showClientIdSearch,
  addClientActionRef,
  // Clients > Document Chasing: list clients from get-document-chasing-clients
  // (only clients that need chasing) instead of get-all-client-data.
  chasingOnly = false,
}) => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [typeOptions, setTypeOptions] = useState([]);
  // Track if typeOptions have been loaded yet (lazy)
  const [typeOptionsLoaded, setTypeOptionsLoaded] = useState(false);

  const { token, user_id, is_admin } = useContext(AuthContext);
  const { permissions } = useRoleContext();

  const [clientAdded, setClientAdded] = useState(0);
  const [clientDeleted, setClientDeleted] = useState(0);
  const [bulkDeleteClient, setBulkDeleteClient] = useState(false);
  const [bulkDeleteClientIds, setBulkDeleteClientIds] = useState();
  const [showBulkChasing, setShowBulkChasing] = useState(false);
  const [sorting, setSorting] = useState([]);
  const [columnFilters, setColumnFilters] = useState([]);
  const [columnVisibility, setColumnVisibility] = useState({});
  const [rowSelection, setRowSelection] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [globalFilter, setGlobalFilter] = useState("");
  const [debouncedGlobalFilter] = useDebounce(globalFilter, 1000);
  const [showFilterButton, setShowFilterButton] = useState(false);
  const [updatedAt, setUpdatedAt] = useState(null);

  const prevFilterParamsRef = useRef({});
  const prevStateRef = useRef({
    updatedAt,
    debouncedGlobalFilter,
    columnFilters,
  });

  let idSearch = "";
  if (clientId !== undefined && !showClientIdSearch) {
    idSearch = `&contact_id=${clientId}`;
  }

  const hasPermission = useCallback(
    (permission) => {
      if (is_admin === "1") return true;
      return permissions?.includes(permission);
    },
    [is_admin, permissions],
  );

  const canEditClient = hasPermission("edit_clients");
  const canDeleteClient = hasPermission("delete_clients");
  const shouldShowActionColumn = canEditClient;

  // ─── Lazy-load typeOptions only when needed (Edit or Add) ───────────────────
  const loadTypeOptionsIfNeeded = useCallback(async () => {
    if (typeOptionsLoaded) return;
    const result = await getAllTypeList({ token, type: "client_type" });
    if (result.code === 200 && result?.data) {
      setTypeOptions(
        result.data.map((t) => ({
          label: t.fields_name,
          typeId: t.field_setting_id,
        })),
      );
    }
    setTypeOptionsLoaded(true);
  }, [token, typeOptionsLoaded]);

  // Called when the Add Client button is clicked
  const handleAddClientClick = useCallback(async () => {
    await loadTypeOptionsIfNeeded();
    onAddClientButtonClick();
  }, [loadTypeOptionsIfNeeded, onAddClientButtonClick]);

  useEffect(() => {
    if (!addClientActionRef) return undefined;
    addClientActionRef.current = handleAddClientClick;
    return () => {
      addClientActionRef.current = onAddClientButtonClick;
    };
  }, [addClientActionRef, handleAddClientClick, onAddClientButtonClick]);

  const clientResponseFunc = useCallback(async () => {
    setLoading(true);
    const offset = currentPage - 1;
    const filterParams = {};
    columnFilters.forEach((f) => {
      filterParams[f.id] = f.value;
    });

    const filtersChanged =
      JSON.stringify(prevFilterParamsRef.current) !==
      JSON.stringify(filterParams);
    if (filtersChanged) setCurrentPage(1);
    prevFilterParamsRef.current = filterParams;

    const sortParam = sorting
      .map((s) => `${s.id}=${s.desc ? "desc" : "asc"}`)
      .join(",");

    const params = {
      isAdmin: is_admin,
      userId: user_id,
      limit: pageSize,
      offset,
      ...filterParams,
      global_search: debouncedGlobalFilter,
      sort: sortParam,
      ...(updatedAt ? { updated_at: convertToUTC(updatedAt) } : {}),
    };

    // Document Chasing page: its own API (only clients needing chasing);
    // Clients page: the existing clients API, unchanged.
    const listApi = chasingOnly ? "get-document-chasing-clients" : "get-all-client-data";
    const url = `${baseUrl}/api/${listApi}?${new URLSearchParams(params)}${idSearch}`;
    const response = await clientsData({ token, api: url });

    if (response.code === 200) {
      setTotalPages(Math.ceil(response.data[0].allclientsCount / pageSize));
      setData(response.data[0].clients);

      const matchingClient = response.data[0].clients?.find(
        (c) => c.contact_id === clientId,
      );
      if (matchingClient) addClientTab(matchingClient);
    } else {
      setTotalPages(1);
      setData([]);
    }
    setLoading(false);
  }, [
    chasingOnly,
    currentPage,
    columnFilters,
    sorting,
    pageSize,
    debouncedGlobalFilter,
    updatedAt,
    idSearch,
    is_admin,
    user_id,
    token,
    clientId,
    addClientTab,
  ]);

  useEffect(() => {
    const currentState = { updatedAt, debouncedGlobalFilter, columnFilters };
    if (JSON.stringify(prevStateRef.current) !== JSON.stringify(currentState)) {
      if (currentPage !== 1) {
        setCurrentPage(1);
        return;
      }
    }
    prevStateRef.current = currentState;
    clientResponseFunc();
  }, [
    clientAdded,
    clientDeleted,
    bulkDeleteClient,
    currentPage,
    columnFilters,
    debouncedGlobalFilter,
    pageSize,
    sorting,
    updatedAt,
  ]);

  const columns = [
    {
      id: "select",
      header: ({ table }) => (
        <div className="flex items-center">
          <Checkbox
            checked={
              table.getIsAllPageRowsSelected() ||
              (table.getIsSomePageRowsSelected() && "indeterminate")
            }
            onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
            aria-label="Select all"
          />
          {(table.getIsSomePageRowsSelected() || table.getIsAllPageRowsSelected()) && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  className="p-0 !border-none !shadow-none bg-transparent"
                >
                  <ChevronDownIcon />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start">
                <DropdownMenuItem onSelect={() => setShowBulkChasing(true)}>
                  Chase Documents
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      ),
      cell: ({ row }) => (
        <Checkbox
          checked={row.getIsSelected()}
          onCheckedChange={(value) => row.toggleSelected(!!value)}
          aria-label="Select row"
        />
      ),
      enableSorting: false,
      enableHiding: false,
    },
    {
      accessorKey: "name",
      displayName: "Client Name",
      enableHiding: false,
      header: ({ column }) => (
        <Button
          variant="ghost"
          size="sm"
          className="p-0"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        >
          Client Name <CaretSortIcon className="ml-2 h-4 w-4" />
        </Button>
      ),
      cell: ({ row }) => (
        <div
          className="wrap-text max-w-[140px] capitalize cursor-pointer text-blue-500 underline"
          onClick={() => addClientTab({ ...row.original, typeOptions })}
        >
          {row.getValue("name")}
        </div>
      ),
      filterFn: (row, id, value) => value.includes(row.getValue(id)),
    },
    {
      accessorKey: "phone_number",
      displayName: "Phone Number",
      header: ({ column }) => (
        <Button
          variant="ghost"
          size="sm"
          className="p-0"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        >
          Phone Number <CaretSortIcon className="ml-2 h-4 w-4" />
        </Button>
      ),
      cell: ({ row }) => (
        <div className="text-left">{row.getValue("phone_number")}</div>
      ),
      filterFn: (row, id, value) => value.includes(row.getValue(id)),
    },
    {
      accessorKey: "email_id",
      displayName: "Email Id",
      header: ({ column }) => (
        <Button
          variant="ghost"
          size="sm"
          className="p-0"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        >
          Email Id <CaretSortIcon className="ml-2 h-4 w-4" />
        </Button>
      ),
      cell: ({ row }) => (
        <div className="wrap-text text-left">{row.getValue("email_id")}</div>
      ),
      filterFn: (row, id, value) => value.includes(row.getValue(id)),
    },
    {
      accessorKey: "case_client_type",
      displayName: "Client Category",
      header: ({ column }) => (
        <Button
          variant="ghost"
          size="sm"
          className="p-0"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        >
          Client Category <CaretSortIcon className="ml-2 h-4 w-4" />
        </Button>
      ),
      cell: ({ row }) => (
        <div className="capitalize">{row.getValue("case_client_type")}</div>
      ),
      filterFn: (row, id, value) => value.includes(row.getValue(id)),
    },
    {
      accessorKey: "case_sub_type",
      displayName: "Client Sub Category",
      header: ({ column }) => (
        <Button
          variant="ghost"
          size="sm"
          className="p-0 text-wrap"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        >
          Client Sub Category <CaretSortIcon className="ml-2 h-4 w-4" />
        </Button>
      ),
      cell: ({ row }) => (
        <div className="wrap-text capitalize">
          {row.getValue("case_sub_type")}
        </div>
      ),
      filterFn: (row, id, value) => value.includes(row.getValue(id)),
    },
    {
      accessorKey: "client_reference_number",
      displayName: "Reference Number",
      header: ({ column }) => (
        <Button
          variant="ghost"
          size="sm"
          className="p-0"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        >
          Reference Number <CaretSortIcon className="ml-2 h-4 w-4" />
        </Button>
      ),
      cell: ({ row }) => (
        <div className="wrap-text max-w-[100px] text-left">
          {row.getValue("client_reference_number")}
        </div>
      ),
      filterFn: (row, id, value) => value.includes(row.getValue(id)),
    },
    {
      accessorKey: "beneficiary_number",
      displayName: "Beneficiary",
      header: ({ column }) => (
        <Button
          variant="ghost"
          size="sm"
          className="p-0"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        >
          Beneficiary <CaretSortIcon className="ml-2 h-4 w-4" />
        </Button>
      ),
      cell: ({ row }) => (
        <div className="wrap-text max-w-[100px] text-left">
          {row.getValue("beneficiary_number")}
        </div>
      ),
      filterFn: (row, id, value) => value.includes(row.getValue(id)),
    },
    {
      accessorKey: "requested_count",
      displayName: "Requested",
      header: () => "Requested",
      cell: ({ row }) => (
        <div className="text-left">{row.getValue("requested_count") ?? 0}</div>
      ),
      enableSorting: false,
    },
    {
      accessorKey: "uploaded_count",
      displayName: "Uploaded",
      header: () => "Uploaded",
      cell: ({ row }) => (
        <div className="text-left">{row.getValue("uploaded_count") ?? 0}</div>
      ),
      enableSorting: false,
    },
    {
      accessorKey: "updated_at",
      displayName: "Modified At",
      header: ({ column }) => (
        <Button
          variant="ghost"
          size="sm"
          className="p-0"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        >
          Modified At <CaretSortIcon className="ml-2 h-4 w-4" />
        </Button>
      ),
      cell: ({ row }) => (
        <div className="capitalize text-wrap">
          {formatDate(row.getValue("updated_at"))}
        </div>
      ),
      filterFn: (row, id, value) => value.includes(row.getValue(id)),
    },
    {
      id: "actions",
      displayName: "Actions",
      enableHiding: false,
      header: () =>
        shouldShowActionColumn ? (
          <Button variant="ghost" size="sm" className="p-0 !cursor-default">
            Actions
          </Button>
        ) : null,
      cell: ({ row }) => (
        <div className="flex gap-1.5">
          {canEditClient && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger>
                  {/* Load typeOptions lazily when Edit is opened */}
                  <EditClientPopup
                    setClientUpdatedToTrue={() => setClientAdded((c) => c + 1)}
                    tableValues={row.original}
                    typeOptions={typeOptions}
                    onOpen={loadTypeOptionsIfNeeded}
                  />
                </TooltipTrigger>
                <TooltipContent className="bg-gray-600">
                  <p>Edit Client</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
        </div>
      ),
    },
  ];

  const { getStickyCellProps } = createStickyColumns({
    right: ["actions"],
  });

  const table = useReactTable({
    data,
    columns,
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true,
    manualSorting: true,
    getSortedRowModel: getSortedRowModel(),
    getFacetedRowModel: getFacetedRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
    getFilteredRowModel: getFilteredRowModel(),
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: setRowSelection,
    state: { sorting, columnFilters, columnVisibility, rowSelection, pageSize },
  });

  return (
    <>
      {bulkDeleteClient && (
        <BulkDeletePopup
          setClientDeletedToTrue={() => setClientDeleted((c) => c + 1)}
          Clientbulkid={bulkDeleteClientIds}
          bulkDeleteClient={bulkDeleteClient}
          setBulkDeleteClient={setBulkDeleteClient}
          setRowSelection={setRowSelection}
        />
      )}
      {showBulkChasing && (
        <BulkDocumentChasingPopup
          open={showBulkChasing}
          onClose={() => setShowBulkChasing(false)}
          clients={table.getSelectedRowModel().rows.map((r) => r.original)}
          setRowSelection={setRowSelection}
        />
      )}
      <div className="document-table documents-table-page">
        {showAddClientForm ? (
          <AddClient
            typeOptions={typeOptions}
            onAddClientButtonClick={onAddClientButtonClick}
            setClientAddedToTrue={() => setClientAdded((c) => c + 1)}
          />
        ) : (
          <div className="documents-content">
                <div className="document__table">
              <div className="w-full bg-white px-2">
                <div className="flex justify-between items-center py-2 flex-wrap gap-4">
                  <Input
                    placeholder="Search By Client Name..."
                    value={globalFilter}
                    onChange={(e) => {
                      setGlobalFilter(e.target.value);
                      table.setGlobalFilter(e.target.value);
                    }}
                    className="max-w-sm"
                  />
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      onClick={() => setShowFilterButton((v) => !v)}
                    >
                      <FilterListIcon sx={{ fontSize: 18 }} className="mr-1" /> Filter
                    </Button>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="outline" className="ml-auto">
                          <ViewColumnIcon sx={{ fontSize: 18 }} className="mr-1" />
                          Columns <ChevronDownIcon className="ml-2 h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        {table
                          .getAllColumns()
                          .filter((c) => c.getCanHide())
                          .map((column) => (
                            <DropdownMenuCheckboxItem
                              key={column.id}
                              className="capitalize"
                              checked={column.getIsVisible()}
                              onCheckedChange={(v) =>
                                column.toggleVisibility(!!v)
                              }
                            >
                              {column.columnDef.displayName}
                            </DropdownMenuCheckboxItem>
                          ))}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>

                {showFilterButton && (
                  <DataToolbar
                    table={table}
                    apiUrl="/api/get-client-filter-name"
                    updatedAt={updatedAt}
                    setUpdatedAt={setUpdatedAt}
                  />
                )}

                <div className="documents-table-frame rounded-md border">
                  {loading ? (
                    <TableSkeleton />
                  ) : (
                    <div className="w-full overflow-x-auto">
                      <Table className="w-full table-auto">
                        <TableHeader className="sticky top-0 bg-white z-10">
                          {table.getHeaderGroups().map((hg) => (
                            <TableRow key={hg.id}>
                              {hg.headers.map((header) => (
                                <TableHead
                                  key={header.id}
                                  style={
                                    getStickyCellProps(header.column.id, true)
                                      .style
                                  }
                                >
                                  {!header.isPlaceholder &&
                                    flexRender(
                                      header.column.columnDef.header,
                                      header.getContext(),
                                    )}
                                </TableHead>
                              ))}
                            </TableRow>
                          ))}
                        </TableHeader>
                        <TableBody>
                          {table.getRowModel().rows?.length ? (
                            table.getRowModel().rows.map((row) => (
                              <TableRow
                                key={row.id}
                                data-state={row.getIsSelected() && "selected"}
                              >
                                {row.getVisibleCells().map((cell) => (
                                  <TableCell
                                    key={cell.id}
                                    className="py-1 px-2 text-xs truncate"
                                    style={
                                      getStickyCellProps(cell.column.id).style
                                    }
                                  >
                                    {flexRender(
                                      cell.column.columnDef.cell,
                                      cell.getContext(),
                                    )}
                                  </TableCell>
                                ))}
                              </TableRow>
                            ))
                          ) : (
                            <TableRow>
                              <TableCell
                                colSpan={columns.length}
                                className="h-24 text-center"
                              >
                                No records found
                              </TableCell>
                            </TableRow>
                          )}
                        </TableBody>
                      </Table>
                    </div>
                  )}
                </div>
                <div className="flex items-center justify-between space-x-2 py-2 flex-wrap gap-4">
                  <div className="flex items-center gap-5">
                    <div className="flex-1 text-sm text-muted-foreground">
                      {table.getFilteredSelectedRowModel().rows.length} of{" "}
                      {table.getFilteredRowModel().rows.length} row(s) selected.
                    </div>
                    <div className="flex items-center space-x-2">
                      <p className="text-sm font-medium">Rows per page</p>
                      <Select
                        value={`${pageSize}`}
                        onValueChange={(v) => {
                          setPageSize(Number(v));
                          setCurrentPage(1);
                        }}
                      >
                        <SelectTrigger className="w-auto">
                          <SelectValue placeholder={pageSize} />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectGroup>
                            {[10, 20, 30, 40, 50].map((s) => (
                              <SelectItem key={s} value={`${s}`}>
                                {s}
                              </SelectItem>
                            ))}
                          </SelectGroup>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="space-x-2">
                    <PaginationSection
                      setCurrentPage={setCurrentPage}
                      totalPages={totalPages}
                      currentPage={currentPage}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default ClientsTable;
